import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buildApiUrl } from '../../utils/apiUrl';
import { useEmpresaId } from '../../context/EmpresaContext';
import { supabase } from '../../supabase';
import { BarChart2, RefreshCw, Loader2, Printer, Layers } from 'lucide-react';

// ── Tipos ────────────────────────────────────────────────────────────────────
interface Columna { key: string; label: string; grupo?: string; sub?: string; }
interface TipoConsolidado {
  codigo: string;
  nombre: string;
  columnas: Columna[];
  totales: Record<string, number>;
  secciones: number;
  muestreos: number;
  lotes: string[];
  plantas: number;
}

// Semana ISO "NN-AA" desde una fecha (igual que el resto del módulo).
function semanaLabel(d: Date): string {
  const x = new Date(d.valueOf());
  if (x.getDay() === 0) x.setDate(x.getDate() + 1);
  const target = new Date(x.valueOf());
  const dayNr = (x.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const week = 1 + Math.round(
    ((target.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7
  );
  return `${String(week).padStart(2, '0')}-${String(target.getFullYear()).slice(-2)}`;
}
function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const colLabel = (c: Columna) => c.sub || (c.grupo ? (c.label.replace(c.grupo, '').trim() || c.label) : c.label);

export function ReporteMuestreosPage() {
  const empresaId = useEmpresaId();
  const [fecha, setFecha] = useState(() => hoyISO());
  const semana = useMemo(() => (fecha ? semanaLabel(new Date(fecha + 'T00:00:00')) : ''), [fecha]);
  const [tipos, setTipos] = useState<TipoConsolidado[]>([]);
  const [totalMuestreos, setTotalMuestreos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [empresaNombre, setEmpresaNombre] = useState('');

  useEffect(() => {
    supabase.from('empresas').select('nombre').eq('id', empresaId).maybeSingle()
      .then(({ data }) => setEmpresaNombre(data?.nombre || ''));
  }, [empresaId]);

  const cargar = useCallback(async () => {
    if (!semana) return;
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/reportes/consolidado?empresa_id=${empresaId}&semana=${encodeURIComponent(semana)}`));
      const j = await res.json();
      setTipos(j.ok ? (j.tipos || []) : []);
      setTotalMuestreos(j.ok ? (j.total_muestreos || 0) : 0);
    } catch {
      setTipos([]); setTotalMuestreos(0);
    } finally {
      setLoading(false);
    }
  }, [empresaId, semana]);

  useEffect(() => { cargar(); }, [cargar]);

  // Agrupar columnas de un tipo por su "grupo" (para mostrar como en el formato)
  const gruposDe = (cols: Columna[]) => {
    const orden: string[] = [];
    const map = new Map<string, Columna[]>();
    for (const c of cols) {
      const g = c.grupo || '';
      if (!map.has(g)) { map.set(g, []); orden.push(g); }
      map.get(g)!.push(c);
    }
    return orden.map(g => ({ grupo: g, cols: map.get(g)! }));
  };

  const imprimir = () => {
    if (tipos.length === 0) return;
    const esc = (s: any) => String(s ?? '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] as string));
    const hoyStr = new Date().toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const bloques = tipos.map(t => {
      const filas = t.columnas
        .filter(c => (t.totales[c.key] || 0) !== 0)
        .map(c => `<tr><td>${esc(c.grupo ? `${c.grupo} · ` : '')}${esc(colLabel(c))}</td><td class="n">${t.totales[c.key]}</td></tr>`)
        .join('');
      return `<div class="tipo">
        <div class="th">${esc(t.nombre)} — ${t.muestreos} muestreo(s) · ${t.secciones} secciones · lotes ${esc(t.lotes.join(', '))}</div>
        <table><tbody>${filas || '<tr><td colspan="2" class="vac">Sin conteos</td></tr>'}</tbody></table>
      </div>`;
    }).join('');
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
<title>Consolidado de Muestreos · ${esc(semana)}</title><style>
  body{font-family:Arial,sans-serif;color:#111;margin:0;padding:22px 26px;font-size:10pt;}
  .head{border-bottom:2px solid #0ea5e9;padding-bottom:8px;margin-bottom:14px;}
  .emp{font-size:15pt;font-weight:bold;color:#003399;}
  .tit{font-size:12pt;font-weight:600;color:#0ea5e9;}
  .meta{font-size:9pt;color:#555;margin-bottom:12px;}
  .tipo{margin-bottom:16px;break-inside:avoid;}
  .th{background:#1a3a6c;color:#fff;font-weight:bold;padding:5px 8px;font-size:9.5pt;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  table{width:100%;border-collapse:collapse;}
  td{border:1px solid #ccc;padding:3px 8px;font-size:9pt;}
  td.n{text-align:right;font-weight:bold;width:90px;}
  td.vac{color:#999;text-align:center;}
  tr:nth-child(even) td{background:#f6f8fc;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  @page{margin:14mm;}
</style></head><body>
  <div class="head"><div class="emp">${esc(empresaNombre || 'Control de Piña')}</div>
  <div class="tit">Consolidado de Muestreos</div></div>
  <div class="meta">Semana <b>${esc(semana)}</b> · ${totalMuestreos} muestreos ejecutados · Impreso ${esc(hoyStr)}</div>
  ${bloques}
</body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) { document.body.removeChild(iframe); return; }
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => { iframe.contentWindow?.focus(); iframe.contentWindow?.print(); setTimeout(() => document.body.removeChild(iframe), 1000); }, 400);
  };

  const labelCls = 'text-[9px] uppercase tracking-widest mb-0.5';

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'var(--surface)' }}>
      {/* Encabezado */}
      <div className="px-5 py-3 shrink-0 flex items-center gap-4 flex-wrap" style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2">
          <BarChart2 size={20} style={{ color: 'var(--emp-accent-txt)' }} />
          <div>
            <h1 className="text-base font-bold" style={{ color: 'var(--ink)' }}>Consolidado de Muestreos</h1>
            <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Totales por tipo de muestreo, por semana</p>
          </div>
        </div>
        <div className="ml-auto flex items-end gap-4">
          <div>
            <p className={labelCls} style={{ color: 'var(--ink-faint)' }}>Fecha</p>
            <input type="date" className="text-sm bg-transparent outline-none"
              style={{ color: 'var(--ink)', borderBottom: '1px solid var(--line)', padding: '2px 0' }}
              value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div className="flex flex-col items-center">
            <span className="text-[9px] uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Semana</span>
            <span className="text-base font-bold font-mono" style={{ color: 'var(--emp-accent-txt)' }}>{semana || '—'}</span>
          </div>
          <button onClick={cargar} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Consultar
          </button>
          <button onClick={imprimir} disabled={tipos.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)', opacity: tipos.length === 0 ? 0.5 : 1 }}>
            <Printer size={13} /> Imprimir
          </button>
        </div>
      </div>

      {/* Cuerpo */}
      <div className="flex-1 overflow-auto p-5">
        <div className="mb-3 text-xs" style={{ color: 'var(--ink-muted)' }}>
          Semana <b>{semana}</b> · {totalMuestreos} muestreo{totalMuestreos !== 1 ? 's' : ''} ejecutado{totalMuestreos !== 1 ? 's' : ''}
        </div>

        {tipos.length === 0 ? (
          <div className="py-16 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
            {loading ? 'Cargando…' : 'Sin muestreos ejecutados en esta semana.'}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {tipos.map((t) => (
              <div key={t.codigo} className="rounded-lg overflow-hidden flex flex-col"
                style={{ background: 'var(--surface-raised)', border: '1px solid var(--line-dim)' }}>
                {/* Cabecera del tipo */}
                <div className="px-3 py-2" style={{ background: 'var(--surface-deep)', borderBottom: '1px solid var(--line-dim)' }}>
                  <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>{t.nombre}</div>
                  <div className="text-[11px] flex items-center gap-2 flex-wrap" style={{ color: 'var(--ink-faint)' }}>
                    <span className="flex items-center gap-1"><Layers size={11} /> {t.muestreos} muestreo(s) · {t.secciones} secc.</span>
                  </div>
                  <div className="text-[10px] mt-0.5" style={{ color: 'var(--ink-faint)' }}>Lotes: {t.lotes.join(', ') || '—'}</div>
                </div>
                {/* Totales por columna, agrupados */}
                <div className="p-3 space-y-2">
                  {gruposDe(t.columnas).map((g, gi) => {
                    const cols = g.cols.filter(c => (t.totales[c.key] || 0) !== 0);
                    if (cols.length === 0) return null;
                    return (
                      <div key={gi}>
                        {g.grupo && (
                          <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: 'var(--emp-accent-txt)' }}>{g.grupo}</div>
                        )}
                        <div className="flex flex-wrap gap-1.5">
                          {cols.map((c) => (
                            <span key={c.key} className="text-[11px] px-2 py-1 rounded"
                              style={{ background: 'var(--surface-deep)', border: '1px solid var(--line-dim)', color: 'var(--ink-muted)' }}>
                              {colLabel(c)}: <b style={{ color: 'var(--ink)' }}>{t.totales[c.key]}</b>
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {Object.values(t.totales).every(v => !v) && (
                    <div className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Sin conteos registrados.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
